/**
 * A programme visit: the pack's form, live decision support beside it, the medicine list and the
 * return date. Everything runs on the device; saving writes FHIR resources to the encrypted
 * store, and they sync later.
 */
import {
  type CdsOutput,
  type CdsResult,
  type EpisodeOfCare,
  type FormValues,
  type Observation,
  type Patient,
  type PatientRecord,
  type ReferralUrgency,
  type Regimen,
  type Value,
  activeProgrammes,
  addDays,
  applyDerive,
  applySuggestion,
  buildContext,
  currentRegimen,
  enrolmentResources,
  evaluateExpr,
  evaluatePack,
  formatEth,
  isoToEth,
  motherOf,
  parse,
  saveEffects,
  splitRecord,
  visibleConcepts,
  visitResources,
  withCalculated,
} from '@assist/core';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { CdsPanel } from '../components/CdsPanel.js';
import {
  ConceptInput,
  type Entries,
  type Entry,
  parseEntries,
} from '../components/ConceptInput.js';
import { RegimenEditor } from '../components/RegimenEditor.js';
import { app, clock } from '../controller.js';
import { go, useApp } from '../hooks.js';
import { t } from '../i18n.js';
import { activePacks } from '../packs.js';
import { PatientChrome } from '../shell.js';

const ethLabel = (iso: string) => {
  const e = isoToEth(iso);
  return e ? formatEth(e) : iso;
};

/** A refer rule's urgency parameter. */
const urgencyParam = (u: string | undefined): ReferralUrgency =>
  u === 'emergency' ? 'emergency' : u === 'urgent' ? 'urgent' : 'routine';

export function Visit({ id, packId, formId }: { id: string; packId: string; formId?: string }) {
  const a = useApp();
  const active = activePacks(a.packSettings).find((x) => x.pack.id === packId);
  const [patient, setPatient] = useState<Patient | null | undefined>(undefined);
  const [record, setRecord] = useState<PatientRecord | null>(null);
  const [mother, setMother] = useState<{ patient: Patient; observations: Observation[] } | null>(
    null,
  );
  const [entries, setEntries] = useState<Entries>({});
  const [enrol, setEnrol] = useState<string[]>([]);
  const [regimen, setRegimen] = useState<Regimen>({});
  const [applied, setApplied] = useState<Set<string>>(new Set());
  const [returnDays, setReturnDays] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  // Referral items the provider chose NOT to record (all are recorded by default).
  const [notReferring, setNotReferring] = useState<Set<string>>(new Set());
  const [other, setOther] = useState({
    reason: '',
    details: '',
    destination: '',
    urgency: 'urgent' as ReferralUrgency,
    transport: '',
  });
  // Laboratory requests the provider ticked, and whether the danger alerts were acknowledged.
  const [ordering, setOrdering] = useState<Set<string>>(new Set());
  const [acked, setAcked] = useState(false);
  const [defaulted, setDefaulted] = useState(false);
  const today = clock.today();
  // The moment of this visit or assessment: fixed when the form opens, used for the advice shown
  // and for what is saved, so the two agree.
  const [nowIso] = useState(() => clock.now());

  useEffect(() => {
    const s = app.session;
    if (!s) return;
    void (async () => {
      const p = await s.get<Patient>('Patient', id);
      const rec = splitRecord(await s.forPatient(id));
      const mid = motherOf(p);
      if (mid) {
        const mp = await s.get<Patient>('Patient', mid);
        const mrec = splitRecord(await s.forPatient(mid));
        if (mp) setMother({ patient: mp, observations: mrec.observations });
      }
      setPatient(p);
      setRecord(rec);
      setRegimen(currentRegimen(rec.medications));
    })();
  }, [id]);

  const pack = active?.pack;
  const allowDraft = active?.allowDraft ?? false;
  const rulesHidden = !!pack && !allowDraft && !pack.rules.some((r) => r.status === 'approved');
  const form = pack?.forms.find((f) => f.id === formId) ?? pack?.forms[0];

  const computed = useMemo(() => {
    if (!pack || !form || !patient || !record) return null;
    const { values, errors } = parseEntries(pack.concepts, entries);
    const opening = [
      ...enrol,
      ...(form.enrols &&
      !activeProgrammes(record.episodes).includes(form.enrols) &&
      !enrol.includes(form.enrols)
        ? [form.enrols]
        : []),
    ];
    const pending: EpisodeOfCare[] = opening.flatMap((code) => {
      const prog = pack.programmes.find((p) => p.code === code);
      return prog ? [enrolmentResources(prog, id, today, nowIso)[0]] : [];
    });
    const snapshot = (f: FormValues) => ({
      today,
      now: nowIso,
      patient,
      episodes: [...record.episodes, ...pending],
      observations: record.observations,
      medications: record.medications,
      encounters: record.encounters,
      ...(mother ? { mother } : {}),
      form: f,
    });
    const all = withCalculated(pack.concepts, values);
    // show_if and defaults read the pack's derived variables too (high risk, exposure, MEOWS...)
    const env = (c: Record<string, Value>) =>
      applyDerive(pack.derive ?? [], { ...c, form: form.id });
    const visible = visibleConcepts(form, env(buildContext(pack, snapshot(all))));
    const shown: FormValues = Object.fromEntries(
      Object.entries(all).filter(([k]) => visible.has(k)),
    );
    const ctx = buildContext(pack, snapshot(shown));
    const result: CdsResult | null = rulesHidden
      ? null
      : evaluatePack(pack, ctx, { allowDraft, form: form.id });
    const visibleErrors: Record<string, string> = Object.fromEntries(
      Object.entries(errors).filter(([k]) => visible.has(k)),
    );
    for (const sec of form.sections)
      for (const f of sec.fields)
        if (f.required && visible.has(f.concept) && shown[f.concept] === undefined)
          visibleErrors[f.concept] ??= t.required;
    return { shown, visible, ctx, env: env(ctx), result, errors: visibleErrors, opening };
  }, [
    pack,
    form,
    patient,
    record,
    mother,
    entries,
    enrol,
    allowDraft,
    rulesHidden,
    id,
    today,
    nowIso,
  ]);

  // Pre-fill fields that declare a default (the next contact number...), once, from the context.
  useEffect(() => {
    if (defaulted || !computed || !form) return;
    const next: Entries = {};
    for (const sec of form.sections)
      for (const f of sec.fields) {
        if (!f.default || entries[f.concept] !== undefined) continue;
        const v = evaluateExpr(parse(f.default), computed.env);
        if (v !== null && v !== undefined) next[f.concept] = typeof v === 'boolean' ? v : String(v);
      }
    setDefaulted(true);
    if (Object.keys(next).length) setEntries((x) => ({ ...next, ...x }));
  }, [computed, defaulted, form, entries]);

  if (!active || !pack || !form)
    return (
      <section>
        <p class="muted">{t.noPacks}</p>
      </section>
    );
  if (patient === undefined || !record || !computed) return null;
  if (patient === null) return <p class="muted">Not found on this device.</p>;

  const before = currentRegimen(record.medications);
  const enrolled = activeProgrammes(record.episodes);
  const programmeName = (code: string) =>
    pack.programmes.find((p) => p.code === code)?.display ?? code;
  // Manual enrolment ("already diagnosed elsewhere") only in this form's chronic programmes: an
  // episode (a labour) or a programme the form opens by itself is never enrolled by hand here.
  const formProgs = pack.programmes.filter((p) => form.programmes.includes(p.code));
  const canEnrol = (code: string) =>
    !enrolled.includes(code) &&
    !enrol.includes(code) &&
    form.enrols !== code &&
    !formProgs.find((p) => p.code === code)?.episode;
  const episodic = !!form?.series;
  const anyProgramme =
    enrolled.some((c) => pack.programmes.some((p) => p.code === c)) || enrol.length > 0;
  const suggestedDays = computed.result?.nextVisitDays ?? null;
  const daysText = returnDays ?? (suggestedDays ? String(suggestedDays) : '');
  const days = Number(daysText);
  const nextVisit =
    daysText && Number.isInteger(days) && days > 0 && days <= 400 ? addDays(today, days) : null;
  const hasErrors = Object.keys(computed.errors).length > 0;
  const regimenChanged = JSON.stringify(before) !== JSON.stringify(regimen);
  const isEmpty =
    !Object.keys(computed.shown).length && !regimenChanged && !computed.opening.length;
  const advice = (computed.result?.outputs ?? []).filter(
    (o) => o.action !== 'classify' && o.action !== 'next_visit',
  );
  const urgent = advice.filter((o) => o.severity === 'danger' && o.action !== 'block');
  const blocks = advice.filter((o) => o.action === 'block');

  const set = (code: string, e: Entry | undefined) => setEntries((x) => ({ ...x, [code]: e }));
  const apply = (o: CdsOutput) => {
    const next = applySuggestion(pack, regimen, o);
    if (!next) return;
    setRegimen(next);
    setApplied(new Set([...applied, o.ruleId]));
  };

  const save = async () => {
    setShowErrors(true);
    if (hasErrors || isEmpty || blocks.length || !app.session) return;
    // Danger signs are read before the visit is recorded: the first press shows them, the second saves.
    if (urgent.length && !acked) {
      setAcked(true);
      document
        .getElementById('ack-danger')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const effects = saveEffects(computed.result);
      const resources = visitResources({
        formId: form.id,
        close: effects.close,
        deceased: effects.deceased,
        pack,
        patient,
        today,
        nowIso,
        record,
        form: computed.shown,
        enrol: [...new Set([...computed.opening, ...effects.enrol])],
        labOrders: advice
          .filter((o) => o.action === 'order_test' && o.params.test && ordering.has(o.ruleId))
          .map((o) => ({
            test: o.params.test as string,
            label: o.params.label ?? o.title,
            ruleId: o.ruleId,
          })),
        regimen,
        result: computed.result,
        nextVisit,
        referrals: [
          ...advice
            .filter((o) => o.action === 'refer' && !notReferring.has(o.ruleId))
            .map((o) => ({
              reason: o.title,
              urgency: urgencyParam(o.params.urgency),
              ruleId: o.ruleId,
              ruleVersion: o.ruleVersion,
            })),
          ...(other.reason.trim()
            ? [
                {
                  reason: [other.reason.trim(), other.details.trim()].filter(Boolean).join(' — '),
                  urgency: other.urgency,
                  destination: other.destination,
                  transport: other.transport,
                },
              ]
            : []),
        ],
      });
      for (const r of resources) await app.session.save(r);
      app.saved();
      go(`patient/${id}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <section class="visit">
      <PatientChrome id={id} here={`visit:${packId}/${form.id}`} />
      <div class="card">
        <h3>{form.title}</h3>
        <div class="programmes-strip">
          {formProgs.map((p) =>
            enrolled.includes(p.code) ? (
              <span key={p.code} class="chip sev-info">
                {p.display}
              </span>
            ) : enrol.includes(p.code) ? (
              <span key={p.code} class="chip pending">
                {t.enrolling(p.display)}{' '}
                <button
                  type="button"
                  class="link"
                  onClick={() => setEnrol(enrol.filter((c) => c !== p.code))}
                >
                  {t.cancel}
                </button>
              </span>
            ) : null,
          )}
          {!episodic && formProgs.some((p) => canEnrol(p.code)) ? (
            <details class="enrol-manual">
              <summary>{t.alreadyDiagnosed}</summary>
              {formProgs
                .filter((p) => canEnrol(p.code))
                .map((p) => (
                  <button
                    key={p.code}
                    type="button"
                    class="small"
                    onClick={() => setEnrol([...enrol, p.code])}
                  >
                    {t.enrolIn(p.display)}
                  </button>
                ))}
            </details>
          ) : null}
        </div>
      </div>
      {urgent.length ? (
        <div class="alert urgent" role="alert">
          {urgent.map((o) => (
            <p key={o.ruleId}>
              <strong>{o.title}.</strong> {o.message}
            </p>
          ))}
        </div>
      ) : null}
      <div class="visit-grid">
        <div class="visit-form">
          {form.sections.map((s) => {
            const fields = s.fields.filter(
              (f) =>
                computed.visible.has(f.concept) &&
                !pack.concepts.find((x) => x.code === f.concept)?.store_from,
            );
            if (!fields.length) return null;
            return (
              <fieldset key={s.id} class="card">
                <legend>{s.title}</legend>
                {fields.map((f) => {
                  const c = pack.concepts.find((x) => x.code === f.concept);
                  if (!c) return null;
                  return (
                    <ConceptInput
                      key={c.code}
                      concept={c}
                      label={f.label}
                      help={f.help}
                      entry={entries[c.code]}
                      calculated={computed.shown[c.code]}
                      error={
                        showErrors || entries[c.code] !== undefined
                          ? computed.errors[c.code]
                          : undefined
                      }
                      onChange={(e) => set(c.code, e)}
                    />
                  );
                })}
              </fieldset>
            );
          })}
          {pack.drugs.length && (anyProgramme || Object.keys(regimen).length) ? (
            <fieldset class="card">
              <legend>{t.medicines}</legend>
              <RegimenEditor
                drugs={pack.drugs}
                before={before}
                regimen={regimen}
                onChange={setRegimen}
              />
            </fieldset>
          ) : null}
          <fieldset class="card">
            <legend>{episodic ? t.referrals : t.nextVisit}</legend>
            {episodic ? null : (
              <div class="field">
                <label for="return-days">{t.returnIn}</label>
                <div class="num">
                  <input
                    id="return-days"
                    inputMode="numeric"
                    value={daysText}
                    onInput={(e) => setReturnDays((e.target as HTMLInputElement).value)}
                  />
                  {nextVisit ? <span class="unit">{t.returnOn(ethLabel(nextVisit))}</span> : null}
                </div>
                {suggestedDays ? <div class="help">{t.suggestedDays(suggestedDays)}</div> : null}
              </div>
            )}
            <div class="field">
              <label for="other-ref">{t.otherReferral}</label>
              {pack.referral_reasons?.length ? (
                <select
                  id="other-ref"
                  value={other.reason}
                  onChange={(e) => setOther({ ...other, reason: e.currentTarget.value })}
                >
                  <option value="">—</option>
                  {pack.referral_reasons.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="other-ref"
                  value={other.reason}
                  onInput={(e) => setOther({ ...other, reason: e.currentTarget.value })}
                />
              )}
            </div>
            {other.reason.trim() ? (
              <>
                {pack.referral_reasons?.length ? (
                  <div class="field">
                    <label for="other-details">{t.referralDetails}</label>
                    <input
                      id="other-details"
                      value={other.details}
                      onInput={(e) => setOther({ ...other, details: e.currentTarget.value })}
                    />
                  </div>
                ) : null}
                <div class="field">
                  <label for="other-dest">{t.referTo}</label>
                  <input
                    id="other-dest"
                    value={other.destination}
                    onInput={(e) => setOther({ ...other, destination: e.currentTarget.value })}
                  />
                </div>
                <div class="field">
                  <label for="other-urgency">{t.urgencyLabel}</label>
                  <select
                    id="other-urgency"
                    value={other.urgency}
                    onChange={(e) =>
                      setOther({ ...other, urgency: e.currentTarget.value as ReferralUrgency })
                    }
                  >
                    {(['urgent', 'emergency', 'routine'] as const).map((u) => (
                      <option key={u} value={u}>
                        {t.urgencies[u]}
                      </option>
                    ))}
                  </select>
                </div>
                <div class="field">
                  <label for="other-transport">{t.transport}</label>
                  <input
                    id="other-transport"
                    value={other.transport}
                    onInput={(e) => setOther({ ...other, transport: e.currentTarget.value })}
                  />
                </div>
              </>
            ) : null}
          </fieldset>
        </div>
        <CdsPanel
          result={computed.result}
          rulesHidden={rulesHidden}
          noRules={!pack.rules.length}
          canApply={(o) => applySuggestion(pack, regimen, o) !== null || applied.has(o.ruleId)}
          onApply={apply}
          applied={applied}
          canEnrol={canEnrol}
          referring={(o) => !notReferring.has(o.ruleId)}
          onToggleReferral={(o) => {
            const next = new Set(notReferring);
            if (next.has(o.ruleId)) next.delete(o.ruleId);
            else next.add(o.ruleId);
            setNotReferring(next);
          }}
          onEnrol={(code) => setEnrol([...enrol, code])}
          ordering={(o) => ordering.has(o.ruleId)}
          onToggleOrder={(o) => {
            const next = new Set(ordering);
            if (next.has(o.ruleId)) next.delete(o.ruleId);
            else next.add(o.ruleId);
            setOrdering(next);
          }}
          programmeName={programmeName}
        />
      </div>
      {err ? <div class="alert">{err}</div> : null}
      {showErrors && hasErrors ? <div class="alert">{t.fixErrors}</div> : null}
      {showErrors && isEmpty ? <div class="alert">{t.emptyVisit}</div> : null}
      {blocks.length ? (
        <div class="alert" role="alert" data-block>
          {blocks.map((o) => (
            <p key={o.ruleId}>
              <strong>{o.title}.</strong> {o.message}
            </p>
          ))}
        </div>
      ) : null}
      {acked && urgent.length ? (
        <div class="alert urgent" id="ack-danger" role="alert">
          <p>
            <strong>{t.ackDangerTitle}</strong>
          </p>
          {urgent.map((o) => (
            <p key={o.ruleId}>
              <strong>{o.title}.</strong> {o.message}
            </p>
          ))}
          <p>{t.ackDangerSave}</p>
        </div>
      ) : null}
      <div class="actions sticky-actions">
        <button type="button" class="ghost" onClick={() => go(`patient/${id}`)}>
          {t.cancel}
        </button>
        {advice.length ? (
          <button
            type="button"
            class="only-narrow"
            onClick={() => document.getElementById('cds')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t.adviceN(advice.length)}
          </button>
        ) : null}
        <button type="button" class="primary" disabled={busy} onClick={() => void save()}>
          {busy ? t.saving : episodic ? t.saveAssessment : t.saveVisit}
        </button>
      </div>
    </section>
  );
}
